/**
 * O aviso — card 1864753210497959381.
 *
 * Ele é um *side-effect component*: vive ao lado de `<QuotaCheck/>` e `<WorkspaceSideEffects/>`
 * no `WorkspaceLayout`, não devolve layout nenhum, e desenha uma faixa `position: fixed`.
 *
 * ⚠️ Montá-lo ali é o que faz o serviço EXISTIR. O framework do Affine resolve serviço sob
 * demanda: um `configure…` sem ninguém pedindo o serviço registra uma classe que nunca é
 * construída — a família de defeitos que o `CLAUDE.md` cataloga desde a regra 8.1, e que a
 * 8.52 pagou de novo com um bloco que nunca renderizou.
 */

import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import { mereceAviso, temFilaPendente } from '../relogio-da-fila';
import { SaudeDaSincronizacaoService } from '../services/saude-da-sincronizacao';
import { AVISO_AO_FECHAR, redigir, redigirSelo } from '../texto';
import * as estilos from './aviso.css';

export const AvisoDeSincronizacao = () => {
  const saude = useService(SaudeDaSincronizacaoService);
  const estado = useLiveData(saude.estado$);
  const prova = useLiveData(saude.prova$);
  const provando = useLiveData(saude.provando$);
  const [encolhido, setEncolhido] = useState(false);

  const visivel = mereceAviso(estado);
  const temFila = temFilaPendente(estado);

  // Ao fechar a aba com fila pendente. O texto é ignorado por todo navegador desde 2017 —
  // eles desenham um diálogo genérico —, então a frase que a pessoa lê de verdade é a da
  // FAIXA. Este gancho serve para o gesto não ser silencioso, não para explicar nada.
  useEffect(() => {
    if (!temFila) return;
    const aoFechar = (evento: BeforeUnloadEvent) => {
      if (!saude.temTrabalhoNaoSalvo()) return;
      evento.preventDefault();
      evento.returnValue = AVISO_AO_FECHAR;
      return AVISO_AO_FECHAR;
    };
    window.addEventListener('beforeunload', aoFechar);
    return () => window.removeEventListener('beforeunload', aoFechar);
  }, [temFila, saude]);

  // Voltar a crescer quando a situação PIORA. Sem isto, encolher uma vez viraria silêncio
  // permanente, e o aviso passaria a esconder justamente o que ficou pior.
  useEffect(() => {
    if (estado.gravidade === 'erro') setEncolhido(false);
  }, [estado.gravidade]);

  useEffect(() => {
    if (prova?.veredito === 'servidor-atrasado') setEncolhido(false);
  }, [prova?.veredito, prova?.medidoEm]);

  // Fila resolvida: o aviso volta ao estado cheio da próxima vez que aparecer.
  useEffect(() => {
    if (!visivel) setEncolhido(false);
  }, [visivel]);

  const conferir = useCallback(() => {
    void saude.provar();
  }, [saude]);

  if (!visivel) return null;

  if (encolhido) {
    return (
      <button
        type="button"
        className={estilos.selo}
        onClick={() => setEncolhido(false)}
        data-testid="tuninho-aviso-sincronizacao-selo"
      >
        <span className={estilos.pontinho} />
        {redigirSelo(estado)}
      </button>
    );
  }

  const texto = redigir(estado, prova);

  return (
    <div
      className={`${estilos.faixa} ${
        texto.tom === 'alerta' ? estilos.faixaAlerta : estilos.faixaAtencao
      }`}
      role="status"
      aria-live="polite"
      data-testid="tuninho-aviso-sincronizacao"
      data-gravidade={estado.gravidade}
      data-prova={prova?.veredito ?? 'sem-prova'}
    >
      <div className={estilos.corpo}>
        <div className={estilos.titulo}>{texto.titulo}</div>
        <div className={estilos.detalhe}>{texto.detalhe}</div>
        {texto.prova ? (
          <div className={estilos.linhaDaProva} data-testid="tuninho-aviso-prova">
            {texto.prova}
          </div>
        ) : null}
      </div>
      <div className={estilos.botoes}>
        <button
          type="button"
          className={estilos.botao}
          onClick={conferir}
          disabled={provando}
          data-testid="tuninho-aviso-conferir"
        >
          {provando ? 'Conferindo…' : texto.acao}
        </button>
        <button
          type="button"
          className={estilos.botao}
          onClick={() => setEncolhido(true)}
          data-testid="tuninho-aviso-encolher"
        >
          Encolher
        </button>
      </div>
    </div>
  );
};
